<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { cortexProcessingRunsAPI } from 'dashboard/api/cortex';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';

const { t } = useI18n();
const route = useRoute();
const detailsDialog = ref(null);
const mediaPreviewDialog = ref(null);
const selectedRun = ref(null);
const processingRunId = ref(null);
const selectedMedia = ref(null);
const detailsLoading = ref(false);
const targetPage = ref(1);
const targetPerPage = ref(10);
let detailsRequestId = 0;
const targetHeaders = computed(() => [
  t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.MESSAGE'),
  t('CORTEX.PROCESSING.MEDIA_RESULTS.TABLE.STATUS'),
  t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.MEDIA'),
  t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.ERROR'),
  t('CORTEX.PROCESSING.MEDIA_RESULTS.TABLE.ACTIONS'),
]);
const statusLabels = computed(() => ({
  pending: t('CORTEX.PROCESSING.MEDIA_RESULTS.STATUSES.PENDING'),
  running: t('CORTEX.PROCESSING.MEDIA_RESULTS.STATUSES.RUNNING'),
  processing: t('CORTEX.PROCESSING.MEDIA_RESULTS.STATUSES.RUNNING'),
  completed: t('CORTEX.PROCESSING.MEDIA_RESULTS.STATUSES.COMPLETED'),
  partial: t('CORTEX.PROCESSING.MEDIA_RESULTS.STATUSES.PARTIAL'),
  failed: t('CORTEX.PROCESSING.MEDIA_RESULTS.STATUSES.FAILED'),
}));
const statusColor = status =>
  ({
    pending: 'amber',
    running: 'brand',
    processing: 'brand',
    completed: 'teal',
    partial: 'amber',
    failed: 'ruby',
  })[status] || 'slate';
const mediaSourceUnavailable = computed(() =>
  [
    'ActiveStorage::FileNotFoundError',
    'empty_file',
    'file_not_attached',
  ].includes(selectedMedia.value?.error_code)
);
const mediaPreviewType = computed(() => {
  const contentType = selectedMedia.value?.content_type?.toLowerCase() || '';
  const fileType = selectedMedia.value?.file_type;

  if (contentType.startsWith('image/') || fileType === 'image') return 'image';
  if (contentType.startsWith('audio/') || fileType === 'audio') return 'audio';
  if (contentType.startsWith('video/') || fileType === 'video') return 'video';

  return 'document';
});
const messageUrl = target =>
  frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: target.conversation_display_id,
    }),
    { messageId: target.message_id }
  );
const loadRunDetails = async () => {
  if (!selectedRun.value) return;

  detailsRequestId += 1;
  const requestId = detailsRequestId;
  detailsLoading.value = true;
  try {
    const { data } = await cortexProcessingRunsAPI.media(
      processingRunId.value,
      {
        target_page: targetPage.value,
        target_per_page: targetPerPage.value,
      }
    );
    if (requestId === detailsRequestId && selectedRun.value)
      selectedRun.value = data;
  } catch (error) {
    useAlert(t('CORTEX.PROCESSING.MEDIA_RESULTS.API.DETAIL_ERROR'));
  } finally {
    if (requestId === detailsRequestId) detailsLoading.value = false;
  }
};

const openRunDetails = async run => {
  processingRunId.value = run.id;
  selectedRun.value = { id: run.id, messages: [] };
  targetPage.value = 1;
  targetPerPage.value = 10;
  detailsDialog.value?.open();
  await loadRunDetails();
};

const closeRunDetails = () => {
  detailsRequestId += 1;
  detailsLoading.value = false;
  selectedRun.value = null;
};

const openMediaPreview = attachment => {
  selectedMedia.value = attachment;
  mediaPreviewDialog.value?.open();
};

const closeMediaPreview = () => {
  selectedMedia.value = null;
};

const updateTargetPage = async value => {
  targetPage.value = value;
  await loadRunDetails();
};

const updateTargetPageSize = async value => {
  targetPerPage.value = value;
  targetPage.value = 1;
  await loadRunDetails();
};

defineExpose({ open: openRunDetails });
</script>

<template>
  <Dialog
    ref="detailsDialog"
    width="6xl"
    position="top"
    overflow-y-auto
    :title="
      $t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.TITLE', {
        id: processingRunId,
      })
    "
    :description="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.DESCRIPTION')"
    :cancel-button-label="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.CLOSE')"
    cancel-button-icon="i-lucide-x"
    :show-confirm-button="false"
    @close="closeRunDetails"
  >
    <div v-if="selectedRun" class="grid gap-4">
      <div class="flex justify-end">
        <NextButton
          type="button"
          sm
          slate
          ghost
          icon="i-lucide-refresh-cw"
          :label="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.REFRESH')"
          :is-loading="detailsLoading"
          @click="loadRunDetails"
        />
      </div>
      <BaseTable
        :headers="targetHeaders"
        :items="selectedRun.messages || []"
        :loading="detailsLoading"
        :loading-message="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.LOADING')"
        :no-data-message="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="target in items" :key="target.id" :item="target">
            <BaseTableCell>
              <div class="grid min-w-40 gap-1">
                <span class="font-medium text-n-slate-12">
                  {{
                    $t(
                      'CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.MESSAGE_VALUE',
                      {
                        conversation: target.conversation_display_id,
                        message: target.message_id,
                      }
                    )
                  }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ target.inbox_name }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell class="align-middle">
              <Label
                compact
                :label="statusLabels[target.status] || target.status"
                :color="statusColor(target.status)"
              />
            </BaseTableCell>
            <BaseTableCell class="align-middle">
              <div class="grid min-w-64 gap-2">
                <div
                  v-for="attachment in target.attachments"
                  :key="attachment.id"
                  class="flex items-center gap-2 border-b border-n-weak pb-2 last:border-b-0 last:pb-0"
                >
                  <div class="min-w-0 flex-1">
                    <span
                      class="block truncate text-sm text-n-slate-12"
                      :title="attachment.filename"
                    >
                      {{ attachment.filename }}
                    </span>
                    <span
                      v-if="attachment.error_message"
                      class="block max-w-64 truncate text-xs text-n-ruby-11"
                      :title="attachment.error_message"
                    >
                      {{ attachment.error_message }}
                    </span>
                  </div>
                </div>
              </div>
            </BaseTableCell>
            <BaseTableCell class="align-middle">
              <span
                class="block max-w-64 truncate text-n-slate-11"
                :title="target.error_message || undefined"
              >
                {{
                  target.error_message ||
                  $t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.NO_ERROR')
                }}
              </span>
            </BaseTableCell>
            <BaseTableCell align="end" class="align-middle">
              <div class="inline-flex items-center justify-end gap-1">
                <a
                  :href="messageUrl(target)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex size-8 items-center justify-center rounded-lg !text-[color:inherit] outline-1 outline-transparent hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
                  :title="
                    $t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.OPEN_MESSAGE')
                  "
                  :aria-label="
                    $t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.OPEN_MESSAGE')
                  "
                >
                  <span
                    class="i-lucide-message-square-text size-4"
                    aria-hidden="true"
                  />
                </a>
                <NextButton
                  v-for="attachment in target.attachments.filter(
                    item => item.media_url
                  )"
                  :key="attachment.id"
                  type="button"
                  sm
                  slate
                  ghost
                  icon="i-lucide-eye"
                  class="!text-[color:inherit]"
                  :title="
                    $t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.OPEN_MEDIA')
                  "
                  :aria-label="
                    $t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.OPEN_MEDIA')
                  "
                  @click="openMediaPreview(attachment)"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
      <PaginationFooter
        :current-page="selectedRun.message_pagination?.page || 1"
        :items-per-page="selectedRun.message_pagination?.per_page || 10"
        :total-items="selectedRun.message_pagination?.total || 0"
        show-page-size-selector
        @update:current-page="updateTargetPage"
        @update:items-per-page="updateTargetPageSize"
      />
    </div>
  </Dialog>

  <Dialog
    ref="mediaPreviewDialog"
    width="6xl"
    overflow-y-auto
    :title="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.OPEN_MEDIA')"
    :description="selectedMedia?.filename"
    :show-confirm-button="false"
    :cancel-button-label="$t('CORTEX.PROCESSING.MEDIA_RESULTS.DETAILS.CLOSE')"
    cancel-button-icon="i-lucide-x"
    @close="closeMediaPreview"
  >
    <div
      v-if="selectedMedia"
      class="flex min-h-72 flex-col items-center justify-center gap-3 overflow-auto rounded-lg bg-n-solid-2 p-3"
    >
      <p
        v-if="selectedMedia.error_message"
        role="alert"
        class="w-full break-words text-sm text-n-ruby-11"
      >
        {{ selectedMedia.error_message }}
      </p>
      <img
        v-if="!mediaSourceUnavailable && mediaPreviewType === 'image'"
        :key="selectedMedia.id"
        :src="selectedMedia.media_url"
        :alt="selectedMedia.filename"
        class="max-h-[70vh] max-w-full object-contain"
      />
      <audio
        v-else-if="!mediaSourceUnavailable && mediaPreviewType === 'audio'"
        :key="selectedMedia.id"
        controls
        preload="metadata"
        class="w-full max-w-2xl"
        :aria-label="selectedMedia.filename"
      >
        <source
          :src="selectedMedia.media_url"
          :type="selectedMedia.content_type"
        />
      </audio>
      <video
        v-else-if="!mediaSourceUnavailable && mediaPreviewType === 'video'"
        :key="selectedMedia.id"
        controls
        preload="metadata"
        class="max-h-[70vh] max-w-full"
        :aria-label="selectedMedia.filename"
      >
        <source
          :src="selectedMedia.media_url"
          :type="selectedMedia.content_type"
        />
      </video>
      <iframe
        v-else-if="!mediaSourceUnavailable"
        :key="selectedMedia.id"
        :src="selectedMedia.media_url"
        :title="selectedMedia.filename"
        class="h-[70vh] w-full rounded-lg border-0 bg-n-solid-1"
      />
    </div>
  </Dialog>
</template>
